// src/components/LevelSelectionScreen.js
import React from 'react';
import { Star, Lock, Trophy, ArrowLeft } from 'lucide-react';
import AyseLadyLogo from './AyseLadyLogo';
import { levels, isLevelUnlocked, getLevelProgress } from '../data/levels';

const LevelSelectionScreen = ({ userProgress, onSelectLevel, onBackToMenu }) => {
  const completedLevels = Object.keys(userProgress).filter(levelId => 
    userProgress[levelId]?.completed
  ).map(Number);

  const totalCompleted = completedLevels.length;
  const overallProgress = (totalCompleted / levels.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-400 via-purple-500 to-pink-500 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={onBackToMenu}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={20} className="mr-2" />
              Ana Menü
            </button>
            <AyseLadyLogo />
            <div className="text-right">
              <div className="text-lg font-bold text-gray-700">
                {totalCompleted}/20 <Trophy className="inline w-5 h-5 text-yellow-500" />
              </div>
              <div className="text-sm text-gray-500">Tamamlanan</div>
            </div>
          </div>

          {/* Overall Progress */}
          <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
            <div 
              className="bg-gradient-to-r from-green-400 to-blue-500 h-4 rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            ></div>
          </div>
          <div className="text-center text-sm text-gray-600">
            Genel İlerleme: %{Math.round(overallProgress)}
          </div>
        </div>

        {/* Level Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {levels.map((level) => {
            const isUnlocked = isLevelUnlocked(level.id, completedLevels);
            const progress = getLevelProgress(level.id, userProgress);
            const isCompleted = progress.completed;
            
            return (
              <div
                key={level.id}
                className={`relative bg-white rounded-xl shadow-lg p-4 transition-all duration-300 ${
                  isUnlocked 
                    ? 'hover:scale-105 hover:shadow-xl cursor-pointer transform' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={() => isUnlocked && onSelectLevel(level.id)}
              >
                {/* Level Number */}
                <div className="absolute -top-2 -left-2 w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  {level.id}
                </div>

                {/* Completion Status */}
                {isCompleted && (
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center">
                    <Star size={16} className="fill-current" />
                  </div>
                )}

                {/* Lock Icon */}
                {!isUnlocked && (
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <Lock size={24} className="text-gray-400" />
                  </div>
                )}

                {/* Level Content */}
                <div className={`text-center ${!isUnlocked ? 'blur-sm' : ''}`}>
                  <div className="text-3xl mb-2">{level.emoji}</div>
                  <h3 className="font-bold text-sm text-gray-800 mb-1 leading-tight">
                    {level.title}
                  </h3>
                  <p className="text-xs text-gray-600 mb-2">
                    {level.description}
                  </p>
                  
                  {/* Progress for unlocked levels */}
                  {isUnlocked && (
                    <div className="mt-2">
                      {isCompleted ? (
                        <div className="text-xs text-green-600 font-semibold">
                          ✓ Tamamlandı
                        </div>
                      ) : progress.attempts > 0 ? (
                        <div className="text-xs text-blue-600">
                          En İyi: {progress.score}/10
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500">
                          Henüz başlanmadı
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Difficulty Indicator */}
                <div className="flex justify-center mt-2">
                  {[...Array(Math.min(Math.ceil(level.id / 4), 5))].map((_, i) => (
                    <Star 
                      key={i} 
                      size={12} 
                      className={`mx-0.5 ${
                        isUnlocked ? 'text-yellow-400' : 'text-gray-300'
                      }`} 
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Cat Progress */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 mt-6 text-center">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Kedi Arkadaşın</h3>
          <div 
            className="text-8xl transition-all duration-500 mx-auto mb-4"
            style={{ 
              filter: `brightness(${0.5 + (overallProgress / 100) * 0.5})`,
              transform: `scale(${0.8 + (overallProgress / 100) * 0.4})`
            }}
          >
            🐱
          </div>
          <div className="text-lg font-semibold text-gray-700 mb-2">
            {overallProgress < 25 ? "Yavru Kedi 🐱" : 
             overallProgress < 50 ? "Genç Kedi 😸" : 
             overallProgress < 75 ? "Yetişkin Kedi 😺" : 
             overallProgress === 100 ? "Usta Kedi 😻" : "Büyüyen Kedi 😸"}
          </div>
          <div className="text-sm text-gray-500">
            {Math.round(overallProgress)}% büyüdü
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-3">
            <div 
              className="bg-gradient-to-r from-pink-400 to-purple-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelSelectionScreen;